import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import InvestorPortalLayout from '@/layouts/InvestorPortalLayout'

interface Offering {
  id: number
  loan_identifier: string
  proposed_principal_amount: number
  proposed_ownership_percent: number
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'withdrawn'
  created_at: string
  expires_at: string | null
  accepted_at: string | null
  declined_at: string | null
  message: string | null
  lender_business_name: string
  loan_amount: number
  annual_rate: number
  term_months: number
  loan_status: string
  start_date: string | null
}

interface Props {
  offering: Offering
}

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
}

export default function OfferingShow({ offering }: Props) {
  const isPending = offering.status === 'pending'
  const isExpired = offering.expires_at && new Date(offering.expires_at) < new Date()
  const [showAcceptModal, setShowAcceptModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [processing, setProcessing] = useState(false)

  const handleAccept = () => {
    setProcessing(true)
    router.post(`/investor-portal/offerings/${offering.id}/accept`, {}, {
      onFinish: () => setProcessing(false),
    })
  }

  const handleDecline = () => {
    setProcessing(true)
    router.post(`/investor-portal/offerings/${offering.id}/decline`, {}, {
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <InvestorPortalLayout>
      <div className="mb-6">
        <Link
          href="/investor-portal/offerings"
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Offerings
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Offering — {offering.loan_identifier}</h1>
          <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border ${statusStyles[offering.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
            {offering.status.charAt(0).toUpperCase() + offering.status.slice(1)}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          From {offering.lender_business_name} — received {fmtDate(offering.created_at)}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Proposed Terms */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Proposed Terms</h2>
          <dl className="space-y-3">
            <Row label="Proposed Principal" value={fmtMoney(offering.proposed_principal_amount)} />
            <Row label="Proposed Ownership" value={`${offering.proposed_ownership_percent}%`} />
            {offering.expires_at && (
              <Row
                label="Expires"
                value={fmtDate(offering.expires_at)}
                warn={isExpired ? true : false}
              />
            )}
            {offering.accepted_at && <Row label="Accepted" value={fmtDate(offering.accepted_at)} />}
            {offering.declined_at && <Row label="Declined" value={fmtDate(offering.declined_at)} />}
          </dl>
        </div>

        {/* Loan Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Loan Details</h2>
          <dl className="space-y-3">
            <Row label="Loan" value={offering.loan_identifier} />
            <Row label="Total Loan Amount" value={fmtMoney(offering.loan_amount)} />
            <Row label="Annual Rate" value={`${offering.annual_rate}%`} />
            <Row label="Term" value={`${offering.term_months} months`} />
            <Row label="Loan Status" value={offering.loan_status.charAt(0).toUpperCase() + offering.loan_status.slice(1)} />
            {offering.start_date && <Row label="Start Date" value={fmtDate(offering.start_date)} />}
          </dl>
        </div>
      </div>

      {/* Lender Message */}
      {offering.message && (
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Message from Lender</h2>
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {offering.message}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {isPending && !isExpired && (
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => setShowAcceptModal(true)}
            className="px-5 py-2.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            Accept Offering
          </button>
          <button
            onClick={() => setShowDeclineModal(true)}
            className="px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Decline Offering
          </button>
        </div>
      )}

      {isPending && isExpired && (
        <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-sm text-amber-800">This offering has expired. Contact your lender if you're still interested.</p>
        </div>
      )}

      <div className="mt-6 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
        <p className="text-xs text-gray-500">
          Borrower details are intentionally hidden. Contact your lender directly for borrower-specific information.
        </p>
      </div>

      {/* Accept Confirmation Modal */}
      {showAcceptModal && (
        <Modal onClose={() => setShowAcceptModal(false)}>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Accept this offering?</h3>
          <p className="text-sm text-gray-600 mb-5">
            You're accepting {offering.proposed_ownership_percent}% ownership ({fmtMoney(offering.proposed_principal_amount)} principal) of {offering.loan_identifier}. This creates a participation record. Continue?
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowAcceptModal(false)}
              disabled={processing}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={processing}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {processing ? 'Processing...' : 'Confirm Accept'}
            </button>
          </div>
        </Modal>
      )}

      {/* Decline Confirmation Modal */}
      {showDeclineModal && (
        <Modal onClose={() => setShowDeclineModal(false)}>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Decline this offering?</h3>
          <p className="text-sm text-gray-600 mb-5">
            Decline this offering? You can't undo this.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowDeclineModal(false)}
              disabled={processing}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDecline}
              disabled={processing}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            >
              {processing ? 'Processing...' : 'Confirm Decline'}
            </button>
          </div>
        </Modal>
      )}
    </InvestorPortalLayout>
  )
}

function Row({ label, value, warn = false }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className={`text-sm font-medium ${warn ? 'text-amber-600' : 'text-gray-900'}`}>{value}</dd>
    </div>
  )
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        {children}
      </div>
    </div>
  )
}
